import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useToast } from './ui';

interface Note {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  entity: string | null;
  entityId: string | null;
  readAt: string | null;
  at: string;
}

/**
 * Where each kind of notification takes you when you click it.
 *
 * The screen, and where the screen can open one thing, the thing itself: an
 * order notification that lands you on a board of forty orders has made you do
 * the finding twice.
 */
const DESTINATION: Record<string, (note: Note) => string> = {
  HOUSEKEEPING_ASSIGNED: () => '/housekeeping',
  ROOM_DIRTY: () => '/housekeeping',
  ORDER_RECEIVED: (note) =>
    note.entityId ? `/online-orders?open=${note.entityId}` : '/online-orders',
  ORDER_ACCEPTED: () => '/kitchen',
  PO_APPROVAL: (note) => (note.entityId ? `/procurement?open=${note.entityId}` : '/procurement'),
  LOW_STOCK: () => '/inventory',
};

/**
 * Telling somebody now, rather than when they next look at the bell.
 *
 * A toast inside the app, and - if they have allowed it - a desktop
 * notification, which is what reaches somebody who has the tab in the
 * background. Deliberately not Firebase: FCM would put a Google account and a
 * service worker between a hotel and its own kitchen, and it only earns that
 * when notifications must arrive with the app closed. The browser's own
 * Notification API covers a screen that is open all shift, which is how this
 * is actually used.
 */
function desktopNotify(note: Note, onClick: () => void): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const shown = new Notification(note.title, {
      body: note.body ?? undefined,
      tag: note.id,
      icon: '/favicon.svg',
    });
    shown.onclick = () => {
      window.focus();
      onClick();
      shown.close();
    };
  } catch {
    // Some browsers refuse to construct one outside a service worker. The
    // toast has already been shown, so there is nothing to recover from.
  }
}

const ago = (iso: string): string => {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

/**
 * The bell.
 *
 * Polls rather than holding a socket open: a hotel runs this behind whatever
 * proxy it already has, and a long-lived connection is the first thing such a
 * setup breaks — quietly, and only in production. Ten seconds is well inside
 * the time it takes anybody to walk to a room.
 */
export function NotificationBell() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  const { push } = useToast();
  /** Ids already announced, so a poll does not re-announce the same thing. */
  const announced = useRef<Set<string> | null>(null);

  const notes = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ unread: number; items: Note[] }>('/api/notifications?limit=20'),
    refetchInterval: 10_000,
    /*
     * Keep polling while the tab is in the background.
     *
     * React Query stops the interval when the window loses focus, which is
     * precisely when a notification matters: the till is in front, the browser
     * is behind, and nothing arrives until somebody clicks back. That is why
     * the bell only ever seemed to fill in while you were looking at it.
     */
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  const items = notes.data?.items ?? [];

  /*
   * Announce anything that arrived since the last poll.
   *
   * The first load seeds the set without announcing: signing in should not
   * fire eight toasts for things that happened yesterday.
   */
  useEffect(() => {
    if (!notes.data) return;
    const unreadNow = items.filter((note) => !note.readAt);

    if (announced.current === null) {
      announced.current = new Set(unreadNow.map((note) => note.id));
      return;
    }

    for (const note of unreadNow) {
      if (announced.current.has(note.id)) continue;
      announced.current.add(note.id);
      push(note.title, 'ok');
      desktopNotify(note, () => openNote(note));
    }
    // Deliberately keyed on the response alone: openNote is redeclared each
    // render and would restart this on every one.
  }, [notes.data]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (event: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['notifications'] });

  const readOne = useMutation({
    mutationFn: (id: string) => api.post(`/api/notifications/${id}/read`, {}),
    onSuccess: invalidate,
  });
  const readAll = useMutation({
    mutationFn: () => api.post('/api/notifications/read-all', {}),
    onSuccess: invalidate,
  });

  const unread = notes.data?.unread ?? 0;

  function openNote(note: Note) {
    if (!note.readAt) readOne.mutate(note.id);
    setOpen(false);
    const to = DESTINATION[note.kind]?.(note);
    if (to) navigate(to);
  }

  /**
   * Desktop notifications are asked for, not assumed.
   *
   * Browsers only allow the prompt from a click, and a prompt nobody asked for
   * is one everybody dismisses - so it sits behind a line in the bell.
   */
  const canAsk = typeof Notification !== 'undefined' && Notification.permission === 'default';

  return (
    <div style={{ position: 'relative' }} ref={popRef}>
      <button
        className="bell"
        aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">◔</span>
        {unread > 0 ? <span className="bell-dot">{unread > 9 ? '9+' : unread}</span> : null}
      </button>

      {open ? (
        <div className="menu-pop notif-pop">
          <div className="head row between">
            <span>Notifications</span>
            {unread > 0 ? (
              <button
                className="btn ghost sm"
                style={{ marginLeft: 'auto' }}
                onClick={() => readAll.mutate()}
              >
                Mark all read
              </button>
            ) : null}
          </div>

          {canAsk ? (
            <button
              className="notif-permission xs"
              onClick={() => void Notification.requestPermission()}
            >
              Also show these on your desktop
            </button>
          ) : null}

          {items.length === 0 ? (
            <div className="notif-empty small muted">Nothing needs you right now.</div>
          ) : (
            <div className="notif-list">
              {items.map((note) => (
                <button
                  key={note.id}
                  className={`notif ${note.readAt ? '' : 'unread'}`}
                  onClick={() => openNote(note)}
                >
                  <span className="notif-title">{note.title}</span>
                  {note.body ? <span className="notif-body">{note.body}</span> : null}
                  <span className="notif-time">{ago(note.at)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
